import { mixed, type AnySchema } from 'yup';
import { type ImageAlignment, type ImageSettings, type ImageSizing } from './contracts';
import {
    assertNoUnknownKeys,
    normalizeRequiredString,
    parseRuntimeBindingFromSettingsByValueKind,
} from '../runtimeBindingSettingsYupUtils';
import { parseContainCoverFit } from './fitUtils';
import { isRecord } from '../../../../../utils/typeGuards';

const IMAGE_ROOT_ALLOWED_KEYS = [
    'binding',
    'alt',
    'fit',
    'width',
    'height',
    'sizing',
    'alignment',
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
] as const;
const IMAGE_ALIGNMENTS = ['left', 'center', 'right'] as const;
const IMAGE_SIZING_MODES = ['auto', 'custom'] as const;

function hasOwnSetting(settings: Record<string, unknown>, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(settings, key);
}

function parseOptionalImageAlignment(value: unknown): ImageAlignment | undefined {
    if (value === null || typeof value === 'undefined' || value === '') {
        return undefined;
    }

    if (
        typeof value !== 'string' ||
        !IMAGE_ALIGNMENTS.includes(value as (typeof IMAGE_ALIGNMENTS)[number])
    ) {
        throw new Error(`Expected "image.alignment" to be one of: ${IMAGE_ALIGNMENTS.join(', ')}.`);
    }

    return value as ImageAlignment;
}

function parseOptionalMargin(value: unknown, path: string): number | undefined {
    if (value === null || typeof value === 'undefined') {
        return undefined;
    }

    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        throw new Error(`Expected "${path}" to be a non-negative finite number.`);
    }

    return value;
}

function parseImageSizing(value: unknown): ImageSizing | undefined {
    if (typeof value === 'undefined') {
        return undefined;
    }

    if (
        typeof value !== 'string' ||
        !IMAGE_SIZING_MODES.includes(value as (typeof IMAGE_SIZING_MODES)[number])
    ) {
        throw new Error(`Expected "image.sizing" to be one of: ${IMAGE_SIZING_MODES.join(', ')}.`);
    }

    return value as ImageSizing;
}

function assertSettingIsOmitted(
    settings: Record<string, unknown>,
    key: 'fit' | 'width' | 'height',
    sizing: 'default' | 'auto'
): void {
    if (hasOwnSetting(settings, key)) {
        throw new Error(`Expected "image.${key}" to be omitted for "${sizing}" image sizing.`);
    }
}

export function normalizeImageSettings(rawSettings: unknown): ImageSettings {
    if (!isRecord(rawSettings)) {
        throw new Error('Expected "image" settings to be an object.');
    }

    assertNoUnknownKeys(rawSettings, IMAGE_ROOT_ALLOWED_KEYS, 'image');

    const alignment = parseOptionalImageAlignment(rawSettings.alignment);
    const marginTop = parseOptionalMargin(rawSettings.marginTop, 'image.marginTop');
    const marginRight = parseOptionalMargin(rawSettings.marginRight, 'image.marginRight');
    const marginBottom = parseOptionalMargin(rawSettings.marginBottom, 'image.marginBottom');
    const marginLeft = parseOptionalMargin(rawSettings.marginLeft, 'image.marginLeft');
    const sizing = parseImageSizing(rawSettings.sizing);

    const commonSettings = {
        binding: parseRuntimeBindingFromSettingsByValueKind({
            settings: rawSettings,
            settingsPath: 'image',
            valueKind: 'image',
        }),
        alt: normalizeRequiredString(rawSettings.alt, 'image.alt'),
        ...(alignment ? { alignment } : {}),
        ...(typeof marginTop === 'number' ? { marginTop } : {}),
        ...(typeof marginRight === 'number' ? { marginRight } : {}),
        ...(typeof marginBottom === 'number' ? { marginBottom } : {}),
        ...(typeof marginLeft === 'number' ? { marginLeft } : {}),
    };

    if (typeof sizing === 'undefined') {
        assertSettingIsOmitted(rawSettings, 'fit', 'default');
        assertSettingIsOmitted(rawSettings, 'width', 'default');
        assertSettingIsOmitted(rawSettings, 'height', 'default');
        return commonSettings;
    }

    if (sizing === 'auto') {
        assertSettingIsOmitted(rawSettings, 'fit', sizing);
        assertSettingIsOmitted(rawSettings, 'width', sizing);
        assertSettingIsOmitted(rawSettings, 'height', sizing);
        return { ...commonSettings, sizing };
    }

    return {
        ...commonSettings,
        sizing,
        fit: parseContainCoverFit(rawSettings.fit, 'image.fit'),
        width: normalizeRequiredString(rawSettings.width, 'image.width'),
        height: normalizeRequiredString(rawSettings.height, 'image.height'),
    };
}

export function buildImageSettingsYupSchema(): AnySchema {
    return mixed()
        .required('Expected "image" settings to be an object.')
        .test('image-settings-shape', 'Invalid image settings.', function test(value) {
            if (!isRecord(value)) {
                return this.createError({
                    message: 'Expected "image" settings to be an object.',
                });
            }

            try {
                normalizeImageSettings(value);
                return true;
            } catch (error) {
                const normalizedError = error instanceof Error ? error : new Error(String(error));
                return this.createError({ message: normalizedError.message });
            }
        });
}
